import {
  StorylineDilemma,
  StorylineDilemmaOption,
} from '../Types/StorylineDilemma';
import { getApiInstance } from './Api';
import { mapArticleAttribute } from './ArticleAttributeApi';
import { mapUser } from './UserApi';
export function mapStorylineDilemma(dilemmaViewModel: any): StorylineDilemma {
  return {
    ...dilemmaViewModel,
    customizedAt: dilemmaViewModel.customizedAt
      ? new Date(dilemmaViewModel.customizedAt)
      : null,
    customizedBy: dilemmaViewModel.customizedBy
      ? mapUser(dilemmaViewModel.customizedBy)
      : null,
    options: dilemmaViewModel.options.map((optionViewModel: any) =>
      mapStorylineDilemmaOption(optionViewModel, dilemmaViewModel),
    ),
  };
}

export function mapStorylineDilemmaOption(
  optionViewModel: any,
  dilemmaViewModel: any,
): StorylineDilemmaOption {
  return {
    ...optionViewModel,
    image: {
      originalUrl: new URL(
        `dilemmas/${dilemmaViewModel.id}/options/${optionViewModel.id}.png`,
        window.env.REACT_APP_MEDIA_BASE_URL,
      ).toString(),
      sharedStorageUrl: new URL(
        `dilemmas/${dilemmaViewModel.id}/options/${optionViewModel.id}.png`,
        window.env.REACT_APP_SHARED_MEDIA_BASE_URL,
      ).toString(),
    },
    attribute:
      optionViewModel.attribute &&
      mapArticleAttribute(optionViewModel.attribute),
  };
}

export class StorylineApi {
  async getGenericDilemmas(): Promise<StorylineDilemma[]> {
    try {
      const response = await getApiInstance().get(`/storyline/dilemmas`);
      return response.data.map(mapStorylineDilemma);
    } catch (error) {
      console.error('Error fetching generic dilemmas:', error);
      throw error;
    }
  }

  async getChapterDilemmas(
    bookId: string,
    chapterId: string,
  ): Promise<StorylineDilemma[]> {
    try {
      const response = await getApiInstance().get(
        `/storyline/books/${bookId}/chapters/${chapterId}/dilemmas`,
      );
      return response.data.map(mapStorylineDilemma);
    } catch (error) {
      console.error('Error fetching chapter dilemmas:', error);
      throw error;
    }
  }

  async generateDilemmas(bookId: string): Promise<StorylineDilemma[]> {
    const response = await getApiInstance().get(
      `/storyline/dilemmas/${bookId}`,
    );
    return response.data.map(mapStorylineDilemma);
  }

  async createCustomDilemmaOption(
    dilemmaId: string,
    title: string,
    description: string,
  ): Promise<StorylineDilemmaOption> {
    try {
      const response = await getApiInstance().post(
        `/storyline/dilemmas/${dilemmaId}/options`,
        { title, description },
      );
      return mapStorylineDilemmaOption(response.data, { id: dilemmaId });
    } catch (error) {
      console.error('Error creating custom dilemma option:', error);
      throw error;
    }
  }

  /**
   * Saves the selected dilemma options and soft-deletes unselected ones
   * @param dilemmaId The ID of the dilemma
   * @param selectedOptionIds Array of selected option IDs (should be exactly 3)
   */
  async selectDilemmaOptions(
    dilemmaId: string,
    selectedOptionIds: string[],
  ): Promise<void> {
    try {
      await getApiInstance().post(
        `/storyline/dilemmas/${dilemmaId}/select-options`,
        {
          selectedOptionIds,
        },
      );
    } catch (error) {
      console.error('Error saving dilemma choices:', error);
      throw error;
    }
  }

  /**
   * Deletes a dilemma option created by the user
   * @param dilemmaId The ID of the dilemma
   * @param optionId The ID of the option to delete
   */
  async deleteDilemmaOption(
    dilemmaId: string,
    optionId: string,
  ): Promise<void> {
    try {
      await getApiInstance().delete(
        `/storyline/dilemmas/${dilemmaId}/options/${optionId}`,
      );
    } catch (error) {
      console.error('Error deleting dilemma option:', error);
      throw error;
    }
  }
}
